import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", { class: "hidden md:block w-px bg-gray-200 h-auto self-stretch" }, null, -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "block md:hidden w-full h-px bg-gray-200 self-stretch" }, null, -1)
const _hoisted_3 = { class: "flex flex-1 min-w-0 md:min-w-[200px] flex-col gap-1 text-center justify-center md:text-left" }
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = { class: "md:shrink-0" }
const _hoisted_6 = { class: "md:truncate md:min-w-0" }
const _hoisted_7 = { class: "flex flex-col justify-center mt-4" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "w-full h-px bg-gray-200 self-stretch" }, null, -1)
const _hoisted_9 = { class: "flex flex-col gap-1 text-left" }

import { capitalizeFirstLetter } from '@/utils/string'
import { getDutyTagBgClass, getDutyTagBorderClass } from '@/utils/doctorStation'
import { classifyNpsScore } from '@/utils/nps'
import { computed } from 'vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import DoctorInfo from '@/components/DoctorInfo.vue'
import {
  AuthorizationPermissionCheck,
  AuthorizationPermissions,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'


export default /*#__PURE__*/_defineComponent({
  __name: 'CardDutyWithDoctor',
  props: {
    formation: {},
    team: {},
    position: { default: '' },
    shift: { default: '' },
    dutyLabel: {},
    doctorName: {},
    doctorCrm: {},
    active: { type: Boolean },
    layout: { default: 'full' },
    scheduleId: {},
    npsScore: {},
    avatarUrl: {},
    showRequirements: { type: Boolean, default: undefined }
  },
  setup(__props: any) {

const props = __props;



const permissionsStore = usePermissionsStore()

const canReadScheduleRequirements = computed(() => {
  // Tri-state: só entra no override quando o consumidor passou a prop de fato.
  // Depende do default explícito `showRequirements: undefined` no
  // `withDefaults` acima — sem ele o Vue entrega `false` para quem não passa a
  // prop, esta condição fica sempre verdadeira e a permissão nunca é checada.
  if (props.showRequirements !== undefined) {
    return props.showRequirements
  }

  if (!props.scheduleId) {
    return false
  }

  const checkObject: AuthorizationPermissionCheck = {
    permission: AuthorizationPermissions.ScheduleRequerimentReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  }

  return permissionsStore.check(checkObject)
})

const capitalizedDutyLabel = computed(() => {
  return props.dutyLabel ? capitalizeFirstLetter(props.dutyLabel) : ''
})

const dutyBgColor = computed(() => {
  return `${getDutyTagBgClass(props.dutyLabel)} ${getDutyTagBorderClass(props.dutyLabel)}`
})

const npsColors = computed(() => (props.npsScore != null ? classifyNpsScore(props.npsScore) : null))

// Fonte única dos valores e dos fallbacks, consumida pelos dois layouts. O
// gênero difere de propósito: posição e turno são configuração de escala que
// pode não estar vinculada ("não definida/definido"); formação e equipe são
// dados preenchidos ("não informado") — ver #2260.
const requirementValues = computed(() => ({
  formation: props.formation?.trim().length ? props.formation : 'Não informado',
  team: props.team?.trim().length ? props.team : 'Não informado',
  position: props.position?.trim().length ? props.position : 'Não definida',
  shift: props.shift?.trim().length ? capitalizeFirstLetter(props.shift) : 'Não definido'
}))

// Percorrido por `v-for` no layout `full`: a marcação de rótulo + valor truncado
// + tooltip é idêntica nos quatro campos, e repeti-la quatro vezes já custou uma
// divergência antes (o fallback de Posição/Turno ficou fora de sincronia).
const requirementFields = computed(() => [
  { label: 'Formação', value: requirementValues.value.formation, testId: 'duty-card-formation' },
  { label: 'Equipe', value: requirementValues.value.team, testId: 'duty-card-team' },
  { label: 'Posição', value: requirementValues.value.position, testId: 'duty-card-position' },
  { label: 'Turno', value: requirementValues.value.shift, testId: 'duty-card-shift' }
])

// O layout `simple` sempre mostrou só Formação e Equipe — posição e turno não
// cabem num card estreito. Derivado da mesma lista para os rótulos existirem em
// um único lugar.
const simpleRequirementFields = computed(() => requirementFields.value.slice(0, 2))

return (_ctx: any,_cache: any) => {
  const _component_v_tooltip = _resolveComponent("v-tooltip")!

  return (_ctx.layout === 'full')
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["flex flex-col md:flex-row flex-1 relative justify-center p-3 gap-4 md:gap-5 py-4 px-4 md:px-6 border rounded-lg shadow-sm bg-white text-sm leading-relaxed text-left min-w-0 md:min-w-[250px] w-full md:w-fit", _ctx.active ? 'border-primary' : 'border-gray-200'])
      }, [
        (_ctx.dutyLabel)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass([
        'absolute top-2 left-2 text-xs md:text-sm text-gray-600 border px-1 py-0.5 rounded mb-2',
        dutyBgColor.value
      ])
            }, _toDisplayString(capitalizedDutyLabel.value), 3))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: _normalizeClass(["flex flex-col justify-center min-w-0", { 'pt-4': _ctx.dutyLabel }])
        }, [
          _createVNode(DoctorInfo, {
            name: _ctx.doctorName,
            subtitle: _ctx.doctorCrm,
            "avatar-url": _ctx.avatarUrl,
            "nps-score": _ctx.npsScore,
            size: "lg",
            truncate: true
          }, null, 8, ["name", "subtitle", "avatar-url", "nps-score"])
        ], 2),
        (canReadScheduleRequirements.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _hoisted_1,
              _hoisted_2,
              _createElementVNode("div", _hoisted_3, [
                (_ctx.npsScore != null && npsColors.value)
                  ? (_openBlock(), _createElementBlock("p", {
                      key: 0,
                      class: "text-base px-2 py-0.5 rounded w-fit max-w-full border font-semibold whitespace-nowrap truncate",
                      style: _normalizeStyle({
            color: npsColors.value.color,
            backgroundColor: npsColors.value.backgroundColor,
            borderColor: npsColors.value.color
          })
                    }, [
                      _createTextVNode(" Avaliação Paciente: "),
                      _createElementVNode("strong", null, _toDisplayString(_ctx.npsScore), 1)
                    ], 4))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(requirementFields.value, (field) => {
                  return (_openBlock(), _createElementBlock("p", {
                    key: field.label,
                    class: "text-base md:flex md:items-baseline md:gap-1 md:justify-start",
                    "data-testid": field.testId
                  }, [
                    _createElementVNode("strong", _hoisted_5, _toDisplayString(field.label) + ":", 1),
                    _createElementVNode("span", _hoisted_6, _toDisplayString(field.value), 1),
                    _createVNode(_component_v_tooltip, {
                      activator: "parent",
                      location: "top start",
                      "max-width": "320"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(field.label) + ": " + _toDisplayString(field.value), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ], 8, _hoisted_4))
                }), 128))
              ])
            ], 64))
          : _createCommentVNode("", true)
      ], 2))
    : (_ctx.layout === 'simple')
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["relative flex flex-col items-center justify-start p-4 gap-3 border rounded-lg shadow-sm bg-white text-sm text-center min-w-[200px] w-full md:w-fit", _ctx.active ? 'border-primary' : 'border-gray-200'])
        }, [
          (_ctx.dutyLabel)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass([
        'absolute top-2 left-2 text-xs text-gray-600 border px-1 py-0.5 rounded',
        dutyBgColor.value
      ])
              }, _toDisplayString(capitalizedDutyLabel.value), 3))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(DoctorInfo, {
              name: _ctx.doctorName,
              subtitle: _ctx.doctorCrm,
              "avatar-url": _ctx.avatarUrl,
              "nps-score": _ctx.npsScore,
              size: "md"
            }, null, 8, ["name", "subtitle", "avatar-url", "nps-score"])
          ]),
          _hoisted_8,
          _createElementVNode("div", _hoisted_9, [
            (_ctx.npsScore != null && npsColors.value)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 0,
                  class: "text-base px-2 py-0.5 rounded w-fit border font-semibold",
                  style: _normalizeStyle({
          color: npsColors.value.color,
          backgroundColor: npsColors.value.backgroundColor,
          borderColor: npsColors.value.color
        })
                }, [
                  _createTextVNode(" Avaliação Paciente: "),
                  _createElementVNode("strong", null, _toDisplayString(_ctx.npsScore), 1)
                ], 4))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(simpleRequirementFields.value, (field) => {
              return (_openBlock(), _createElementBlock("p", {
                key: field.label,
                class: "text-base"
              }, [
                _createElementVNode("strong", null, _toDisplayString(field.label) + ":", 1),
                _createTextVNode(" " + _toDisplayString(field.value), 1)
              ]))
            }), 128))
          ])
        ], 2))
      : _createCommentVNode("", true)
}
}

})